import type { PortalAudience } from '@/access/portalAccess.types';
import type {
  JobsViewInput,
  DaySequencesFetchWhen,
} from '@/modules/jobs/jobs.viewInput';

export type { DaySequencesFetchWhen };

export type JobDetailTabKey =
  | 'details'
  | 'activity'
  | 'applications'
  | 'timesheets';

export type JobDetailView = {
  audience: PortalAudience;
  tabs: JobDetailTabKey[];
  actions: {
    assignWorker: boolean;
    editJob: boolean;
    showRateSection: boolean;
    showClientName: boolean;
    showWorkerLink: boolean;
    showApplicantInsights: boolean;
    showApplicantDocuments: boolean;
  };
  query: {
    job: { fetch: boolean };
    applications: { fetch: boolean };
    availableWorkers: { fetch: boolean };
    applicationWorkers: { fetch: boolean };
    assignedWorker: { fetch: boolean };
    daySequences: { fetchWhen: DaySequencesFetchWhen };
  };
};

const FULL_TENANT_TABS: JobDetailTabKey[] = [
  'details',
  'activity',
  'applications',
  'timesheets',
];

const RECRUITER_TENANT_TABS: JobDetailTabKey[] = ['details', 'timesheets'];

const CLIENT_TABS: JobDetailTabKey[] = [
  'details',
  'applications',
  'timesheets',
];

const getDetailTabs = ({
  isTenantPortal,
  isTenantRecruiter,
  isClientPortal,
}: JobsViewInput['access']): JobDetailTabKey[] => {
  if (isClientPortal) {
    return CLIENT_TABS;
  }

  if (isTenantRecruiter) {
    return RECRUITER_TENANT_TABS;
  }

  if (isTenantPortal) {
    return FULL_TENANT_TABS;
  }

  return ['details'];
};

export const getJobDetailView = ({
  access,
  allowClientsToSeeProtectedAssets,
}: JobsViewInput): JobDetailView => {
  const { audience, isTenantPortal, isClientPortal, canManageTenantJobs } =
    access;
  return {
    audience,
    tabs: getDetailTabs(access),
    actions: {
      assignWorker: canManageTenantJobs,
      editJob: isTenantPortal || isClientPortal,
      showRateSection: canManageTenantJobs,
      showClientName: !isClientPortal,
      showWorkerLink: canManageTenantJobs,
      showApplicantInsights: isClientPortal,
      showApplicantDocuments:
        isClientPortal && allowClientsToSeeProtectedAssets,
    },
    query: {
      job: { fetch: true },
      applications: { fetch: canManageTenantJobs || isClientPortal },
      availableWorkers: { fetch: canManageTenantJobs },
      applicationWorkers: { fetch: canManageTenantJobs || isClientPortal },
      assignedWorker: { fetch: true },
      daySequences: { fetchWhen: 'tabSelected' },
    },
  };
};
